import {
  EMPTY,
  GRASS,
  MAX_SIZE,
  TENT,
  TREE,
  type Count,
  type TentsDefinition,
  type TentsProgress,
  type TentsSubmission,
} from "./types";

const DEFAULT_RULES = {
  en: "Place tents in open cells so that each tree has its own tent next to it, horizontally or vertically, and each tent its own tree. Tents never touch each other, not even diagonally. The numbers tell how many tents each row and column holds.",
  fr: "Placez des tentes sur les cases libres de sorte que chaque arbre ait sa propre tente à côté de lui, horizontalement ou verticalement, et chaque tente son propre arbre. Deux tentes ne se touchent jamais, même en diagonale. Les nombres indiquent combien de tentes contient chaque ligne et chaque colonne.",
};

export function emptyDefinition(): TentsDefinition {
  return {
    puzzleType: "tents",
    typeVersion: 1,
    rows: 6,
    cols: 6,
    trees: blankRows(6, 6),
    counts: { rows: Array<Count>(6).fill(null), cols: Array<Count>(6).fill(null) },
    rulesText: { ...DEFAULT_RULES },
  };
}

export function clampSize(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(MAX_SIZE, Math.max(1, Math.trunc(value)));
}

export function blankRows(rows: number, cols: number): string[] {
  return Array.from({ length: rows }, () => EMPTY.repeat(cols));
}

/** Crops or pads a row set to the given size, keeping the top-left corner. */
export function resizeRows(source: string[], rows: number, cols: number): string[] {
  return Array.from({ length: rows }, (_, r) => {
    const row = source[r] ?? "";
    return row.length >= cols ? row.slice(0, cols) : row + EMPTY.repeat(cols - row.length);
  });
}

export function resizeCounts(counts: Count[], length: number): Count[] {
  return Array.from({ length }, (_, i) => counts[i] ?? null);
}

function sanitizeRow(raw: unknown, allowed: readonly string[]): string {
  if (typeof raw !== "string") return "";
  return Array.from(raw, (char) => (allowed.includes(char) ? char : EMPTY)).join("");
}

/** Marks never sit on a tree: whatever a stored blob says there is dropped. */
export function clearTrees(marks: string[], trees: string[]): string[] {
  return marks.map((row, r) =>
    Array.from(row, (char, c) => (trees[r]?.[c] === TREE ? EMPTY : char)).join(""),
  );
}

export function emptyProgress(definition: TentsDefinition): TentsProgress {
  return { version: 1, rows: blankRows(definition.rows, definition.cols) };
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(definition: TentsDefinition, raw: unknown): TentsProgress {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return emptyProgress(definition);
  const source = (raw as { rows?: unknown }).rows;
  if (!Array.isArray(source)) return emptyProgress(definition);
  const rows = source.map((row) => sanitizeRow(row, [TENT, GRASS, EMPTY]));
  return {
    version: 1,
    rows: clearTrees(resizeRows(rows, definition.rows, definition.cols), definition.trees),
  };
}

/** The server only reads tents: grass is the player's own note. */
export function serializeSubmission(
  definition: TentsDefinition,
  progress: TentsProgress,
): TentsSubmission {
  return {
    rows: clearTrees(
      resizeRows(progress.rows, definition.rows, definition.cols),
      definition.trees,
    ).map((row) => row.replaceAll(GRASS, EMPTY)),
  };
}

export function formatCounts(counts: Count[]): string {
  return counts.map((n) => (n === null ? "?" : String(n))).join(" ");
}

/** Reads "1 ? 2" as counts; anything that is not a number is an unknown count. */
export function parseCounts(text: string, length: number): Count[] {
  const tokens = text.trim() === "" ? [] : text.trim().split(/[\s,;]+/);
  return Array.from({ length }, (_, i) => {
    const token = tokens[i];
    return token !== undefined && /^\d+$/.test(token) ? Number(token) : null;
  });
}
